import { Fragment, useEffect, useMemo, useState } from "react"
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import type { ActivityLogEntry } from "../../api/dataApi"

function isRadixSelectPortalNode(node: EventTarget | null) {
  return node instanceof Element && node.closest("[data-radix-select-viewport]") != null
}

export type ComponentTableActivityLogDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  loading: boolean
  entries: ActivityLogEntry[]
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  actionFilter: string
  onActionFilterChange: (value: string) => void
  orderBy: "date" | "action"
  onOrderByChange: (value: "date" | "action") => void
  orderDirection: "asc" | "desc"
  onOrderDirectionChange: (value: "asc" | "desc") => void
  groupByUser: boolean
  onGroupByUserChange: (value: boolean) => void
  onOpenEntryItem?: (entry: ActivityLogEntry) => void
  /** When an item details drawer is open on top of this dialog, outside clicks should close it but keep this dialog open. */
  stackedItemDetailsOpen?: boolean
  onDismissStackedItemDetails?: () => void
  /** Filter the collection table by item owner for this user, then close the dialog. */
  onListUserEntries?: (sample: ActivityLogEntry) => void
}

function activityUserGroupKey(entry: ActivityLogEntry) {
  return entry.userEmail || entry.userUid || "Unknown user"
}

function ActivityEntryChanges({ entry }: { entry: ActivityLogEntry }) {
  if (!entry.changes?.length) return null

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50/80 p-2 dark:border-gray-700 dark:bg-gray-900/50">
      <div className="grid grid-cols-[minmax(100px,0.7fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 border-b border-gray-200 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground dark:border-gray-700">
        <span>Field</span>
        <span>Before</span>
        <span>After</span>
      </div>
      {entry.changes.map((change) => (
        <div
          key={change.field}
          className="grid grid-cols-[minmax(100px,0.7fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-3 border-b border-gray-200/70 py-1.5 text-xs last:border-0 dark:border-gray-700/70"
        >
          <span className="font-medium break-words">{change.label || change.field}</span>
          <span className="whitespace-pre-wrap break-words text-muted-foreground">
            {change.before ?? "—"}
          </span>
          <span className="whitespace-pre-wrap break-words">{change.after ?? "—"}</span>
        </div>
      ))}
    </div>
  )
}

export function ComponentTableActivityLogDialog({
  open,
  onOpenChange,
  loading,
  entries,
  total,
  page,
  pageSize,
  onPageChange,
  actionFilter,
  onActionFilterChange,
  orderBy,
  onOrderByChange,
  orderDirection,
  onOrderDirectionChange,
  groupByUser,
  onGroupByUserChange,
  onOpenEntryItem,
  stackedItemDetailsOpen,
  onDismissStackedItemDetails,
  onListUserEntries,
}: ComponentTableActivityLogDialogProps) {
  const [expandedUserKeys, setExpandedUserKeys] = useState(() => new Set<string>())

  useEffect(() => {
    setExpandedUserKeys(new Set())
  }, [entries, groupByUser])

  const groupedRows = useMemo(() => {
    if (!groupByUser) return null
    const grouped = new Map<string, { userLabel: string; entries: ActivityLogEntry[] }>()
    for (const entry of entries) {
      const key = activityUserGroupKey(entry)
      const existing = grouped.get(key)
      if (!existing) {
        grouped.set(key, { userLabel: key, entries: [entry] })
      } else {
        existing.entries.push(entry)
      }
    }
    for (const { entries: groupEntries } of grouped.values()) {
      groupEntries.sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0
        const tb = b.date ? new Date(b.date).getTime() : 0
        return tb - ta
      })
    }
    return [...grouped.entries()]
      .map(([groupKey, { userLabel, entries: groupEntries }]) => ({
        groupKey,
        userLabel,
        entries: groupEntries,
        mostRecentDate: groupEntries[0]?.date ? new Date(groupEntries[0].date) : null,
      }))
      .sort((a, b) => {
        const at = a.mostRecentDate ? a.mostRecentDate.getTime() : 0
        const bt = b.mostRecentDate ? b.mostRecentDate.getTime() : 0
        return bt - at
      })
  }, [entries, groupByUser])

  const toggleUserExpanded = (groupKey: string) => {
    setExpandedUserKeys((prev) => {
      const next = new Set(prev)
      if (next.has(groupKey)) next.delete(groupKey)
      else next.add(groupKey)
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] flex flex-col"
        onPointerDownOutside={(e) => {
          if (!stackedItemDetailsOpen || !onDismissStackedItemDetails) return
          const t = e.detail.originalEvent.target
          if (isRadixSelectPortalNode(t)) return
          e.preventDefault()
          onDismissStackedItemDetails()
        }}
        onEscapeKeyDown={(e) => {
          if (!stackedItemDetailsOpen || !onDismissStackedItemDetails) return
          e.preventDefault()
          onDismissStackedItemDetails()
        }}
      >
        <DialogHeader>
          <DialogTitle>Activity log</DialogTitle>
          <DialogDescription>
            CRUD actions for this collection. Visible to everyone who can read this collection.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-3 items-center py-2">
          <Select
            value={actionFilter || "all"}
            onValueChange={(v) => {
              onActionFilterChange(v === "all" ? "" : v)
              onPageChange(0)
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="create_item">Create item</SelectItem>
              <SelectItem value="update_item">Update item</SelectItem>
              <SelectItem value="delete_item">Delete item</SelectItem>
              <SelectItem value="bulk_delete">Bulk delete</SelectItem>
              <SelectItem value="create_collection">Create collection</SelectItem>
              <SelectItem value="update_collection">Update collection</SelectItem>
              <SelectItem value="delete_collection">Delete collection</SelectItem>
            </SelectContent>
          </Select>
          <Select value={orderBy} onValueChange={(v) => onOrderByChange(v as "date" | "action")}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Sort by date</SelectItem>
              <SelectItem value="action">Sort by action</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={orderDirection}
            onValueChange={(v) => onOrderDirectionChange(v as "asc" | "desc")}
          >
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Newest first</SelectItem>
              <SelectItem value="asc">Oldest first</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 ml-auto">
            <Switch
              checked={groupByUser}
              onCheckedChange={(checked) => onGroupByUserChange(Boolean(checked))}
              aria-label="Group activity by user"
            />
            <span className="text-xs text-gray-600 dark:text-gray-300">Group by user</span>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto border rounded-md">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  {groupByUser ? (
                    <>
                      <th className="text-left p-2 font-medium w-10" aria-hidden />
                      <th className="text-left p-2 font-medium">User</th>
                      <th className="text-left p-2 font-medium">Most recent</th>
                      <th
                        className="text-left p-2 font-medium"
                        title="Count of log entries on this page for each user. List filters the collection table by item owner for that user."
                      >
                        Entries on this page
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="text-left p-2 font-medium">Date</th>
                      <th className="text-left p-2 font-medium">Action</th>
                      <th className="text-left p-2 font-medium">User</th>
                      <th className="text-left p-2 font-medium">Item / Summary</th>
                      <th className="text-left p-2 font-medium">Count</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={groupByUser ? 4 : 5}
                      className="p-4 text-center text-gray-500"
                    >
                      No activity recorded yet.
                    </td>
                  </tr>
                ) : groupByUser && groupedRows ? (
                  groupedRows.flatMap((row) => {
                    const expanded = expandedUserKeys.has(row.groupKey)
                    const header = (
                      <tr
                        key={`g:${row.groupKey}`}
                        className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40"
                      >
                        <td className="p-1 align-middle w-10">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            aria-expanded={expanded}
                            aria-label={expanded ? "Collapse entries" : "Expand entries"}
                            onClick={() => toggleUserExpanded(row.groupKey)}
                          >
                            {expanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </td>
                        <td
                          className="p-2 align-middle cursor-pointer select-none"
                          onClick={() => toggleUserExpanded(row.groupKey)}
                          title="Show entries for this user"
                        >
                          {row.userLabel}
                        </td>
                        <td
                          className="p-2 whitespace-nowrap align-middle cursor-pointer select-none"
                          onClick={() => toggleUserExpanded(row.groupKey)}
                        >
                          {row.mostRecentDate ? row.mostRecentDate.toLocaleString() : "—"}
                        </td>
                        <td className="p-2 align-middle">
                          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-0">
                            <button
                              type="button"
                              className="cursor-pointer select-none text-left font-medium text-foreground hover:underline"
                              onClick={() => toggleUserExpanded(row.groupKey)}
                            >
                              {row.entries.length}
                            </button>
                            {onListUserEntries ? (
                              <>
                                <span className="text-muted-foreground" aria-hidden>
                                  ·
                                </span>
                                <button
                                  type="button"
                                  className="text-sm font-medium text-blue-700 underline-offset-2 hover:underline dark:text-blue-400"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    const sample = row.entries[0]
                                    if (sample) onListUserEntries(sample)
                                  }}
                                >
                                  List
                                </button>
                              </>
                            ) : null}
                          </span>
                        </td>
                      </tr>
                    )
                    if (!expanded) return [header]
                    const detail = (
                      <tr key={`g:${row.groupKey}:detail`} className="border-0">
                        <td colSpan={4} className="p-0 border-0 bg-gray-50/50 dark:bg-gray-950/30">
                          <div className="px-2 pb-3 pt-1 pl-10">
                            <table className="w-full text-xs sm:text-sm border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                              <thead className="bg-gray-100 dark:bg-gray-800/80">
                                <tr>
                                  <th className="text-left p-2 font-medium">Date</th>
                                  <th className="text-left p-2 font-medium">Action</th>
                                  <th className="text-left p-2 font-medium">Item / Summary</th>
                                  <th className="text-left p-2 font-medium">Count</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.entries.map((entry) => {
                                  const hasItem =
                                    Boolean(entry.itemId?.trim()) && Boolean(onOpenEntryItem)
                                  return (
                                    <Fragment key={entry.uid}>
                                      <tr
                                        className={
                                          hasItem
                                            ? "border-t border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/60"
                                            : "border-t border-gray-200 dark:border-gray-700"
                                        }
                                        onClick={() => {
                                          if (hasItem) onOpenEntryItem?.(entry)
                                        }}
                                      >
                                        <td className="p-2 whitespace-nowrap">
                                          {entry.date ? new Date(entry.date).toLocaleString() : "—"}
                                        </td>
                                        <td className="p-2">
                                          {entry.action?.replace(/_/g, " ") ?? "—"}
                                        </td>
                                        <td
                                          className="p-2 max-w-[220px] truncate"
                                          title={entry.itemSummary || entry.itemId || ""}
                                        >
                                          {entry.itemSummary || entry.itemId || "—"}
                                        </td>
                                        <td className="p-2">
                                          {entry.count != null ? entry.count : "—"}
                                        </td>
                                      </tr>
                                      {entry.changes?.length ? (
                                        <tr>
                                          <td colSpan={4} className="px-2 pb-2 pt-0">
                                            <ActivityEntryChanges entry={entry} />
                                          </td>
                                        </tr>
                                      ) : null}
                                    </Fragment>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )
                    return [header, detail]
                  })
                ) : (
                  entries.map((entry) => {
                    const hasItem = Boolean(entry.itemId?.trim()) && Boolean(onOpenEntryItem)
                    return (
                      <Fragment key={entry.uid}>
                        <tr
                          className={
                            hasItem
                              ? "border-t border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800/60"
                              : "border-t border-gray-200 dark:border-gray-700"
                          }
                          onClick={() => {
                            if (hasItem) onOpenEntryItem?.(entry)
                          }}
                        >
                          <td className="p-2 whitespace-nowrap">
                            {entry.date ? new Date(entry.date).toLocaleString() : "—"}
                          </td>
                          <td className="p-2">{entry.action?.replace(/_/g, " ") ?? "—"}</td>
                          <td className="p-2">{entry.userEmail || entry.userUid || "—"}</td>
                          <td
                            className="p-2 max-w-[200px] truncate"
                            title={entry.itemSummary || entry.itemId || ""}
                          >
                            {entry.itemSummary || entry.itemId || "—"}
                          </td>
                          <td className="p-2">{entry.count != null ? entry.count : "—"}</td>
                        </tr>
                        {entry.changes?.length ? (
                          <tr>
                            <td colSpan={5} className="px-2 pb-2 pt-0">
                              <ActivityEntryChanges entry={entry} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500">{total} total</span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => onPageChange(Math.max(0, page - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={(page + 1) * pageSize >= total}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
